import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Smartphone } from 'lucide-react'
import Logo from '../../components/common/Logo'
import Input from '../../components/common/Input'
import Button from '../../components/common/Button'
import { storageService } from '../../services'
import { API_BASE_URL, API_ENDPOINTS } from '../../constants'

function LoginPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // ตรวจสอบว่า input เป็น email หรือ phone
      const isEmail = phone.includes('@')
      const loginData = isEmail
        ? { email: phone, password }
        : { phone, password }

      // เรียก Login API
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      })

      const data = await response.json()

      if (!response.ok) {
        // แสดง validation details ถ้ามี
        if (data.details && Array.isArray(data.details)) {
          const errorMessages = data.details.map((d: any) => `${d.field}: ${d.message}`).join(', ')
          throw new Error(errorMessages)
        }
        throw new Error(data.message || data.error || 'เข้าสู่ระบบไม่สำเร็จ')
      }

      // เก็บ token และข้อมูล user
      if (data.token) {
        storageService.setAuthToken(data.token)
        storageService.setLoggedIn(true)

        if (data.user) {
          storageService.setUser(data.user)
        }

        // Redirect ไปหน้า Home
        navigate('/')
      } else {
        throw new Error('ไม่พบ token')
      }
    } catch (err: any) {
      console.error('Login error:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Text Overlay (ตาม Figma 100%) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image - รูปโคจาก Figma */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/assets/images/login-bg.png)',
          }}
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full text-white p-12">
          {/* Logo - ตาม Figma */}
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-8 shadow-2xl p-4">
            <Logo size="md" variant="icon" showText={false} />
          </div>

          {/* Welcome Text - ตาม Figma */}
          <h1 className="text-6xl font-bold text-center mb-2 drop-shadow-2xl">
            ยินดีต้อนรับสู่
          </h1>
          <h2 className="text-5xl font-bold text-center drop-shadow-2xl">
            เสโนเวท เอไอ
          </h2>
        </div>
      </div>

      {/* Right Side - Login Form (ตาม Figma 100%) */}
      <div className="flex-1 flex items-center justify-center bg-white p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo & Title - ตาม Figma */}
          <div className="text-center mb-10">
            {/* Logo SENOVATE AI พร้อมชื่อโค */}
            <div className="inline-flex items-center justify-center mb-6">
              <Logo size="lg" variant="icon" showText={false} />
            </div>

            <h1 className="text-2xl font-semibold text-text-primary mb-2">
              ยินดีต้อนรับสู่ เสโนเวท เอไอ
            </h1>
            <p className="text-sm text-text-secondary">
              ระบบจัดการฟาร์มโคเนื้อ
            </p>
          </div>

          {/* Login Form - ตาม Figma */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Phone or Email Input */}
            <Input
              label="เบอร์โทรศัพท์หรืออีเมล"
              type="text"
              placeholder="เบอร์โทรศัพท์หรืออีเมล"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              icon={<Smartphone className="h-5 w-5 text-primary-800" />}
              iconPosition="left"
              required
            />

            {/* Password Input */}
            <Input
              label="รหัสผ่าน"
              type={showPassword ? 'text' : 'password'}
              placeholder="รหัสผ่าน"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={
                showPassword ? (
                  <EyeOff className="h-5 w-5 text-text-tertiary hover:text-primary-800 cursor-pointer" />
                ) : (
                  <Eye className="h-5 w-5 text-text-tertiary hover:text-primary-800 cursor-pointer" />
                )
              }
              iconPosition="right"
              onIconClick={() => setShowPassword(!showPassword)}
              required
            />

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary-800 hover:text-primary-900 font-medium transition-colors"
              >
                ลืมรหัสผ่าน ?
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-error-light border border-error rounded-lg p-3 text-sm text-error">
                {error}
              </div>
            )}

            {/* Login Button - ตาม Figma */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </Button>

            {/* Terms Text */}
            <p className="text-xs text-center text-text-tertiary leading-relaxed">
              เมื่อคุณดำเนินการต่อแสดงว่าคุณยอมรับนโยบาย
              <br />
              ข้อกำหนดการให้บริการและนโยบายความเป็นส่วนตัว
            </p>

            {/* Register Link */}
            <div className="text-center pt-4 border-t border-border-light">
              <span className="text-sm text-text-secondary">ไม่มีบัญชี? </span>
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm text-primary-800 hover:text-primary-900 font-semibold transition-colors"
              >
                ลงทะเบียน
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
